/* eslint-disable react/no-array-index-key */
import React, { Component, createRef } from "react";

import cx from "classnames";
import { endOfDay } from "date-fns";
import * as animationFrame from "dom-helpers/animationFrame";
import getWidth from "dom-helpers/width";
import { isEqual } from "lodash";
import memoize from "memoize-one";
import PropTypes from "prop-types";

import DayColumn from "./DayColumn";
import TimeGridHeader from "./TimeGridHeader";
import TimeGutter from "./TimeGutter";
import { inRange, sortEvents } from "./utils/eventLevels";
import { notify } from "./utils/helpers";
import { DayLayoutAlgorithmPropType } from "./utils/propTypes";
import Resources from "./utils/Resources";

export default class TimeGrid extends Component {
	constructor(props) {
		super(props);

		this.state = { gutterWidths: [], isOverflowing: null };

		this.scrollRef = React.createRef();
		this.contentRef = React.createRef();
		this._scrollRatio = null;
		this.firstGutterRef = createRef();
		this.secondGutterRef = createRef();
		this.thirdGutterRef = createRef();
	}

	getSnapshotBeforeUpdate() {
		this.checkOverflow();
		return null;
	}

	componentDidMount() {
		if (this.props.width == null) {
			this.measureGutter();
		}

		this.calculateScroll();
		this.applyScroll();

		window.addEventListener("resize", this.handleResize);
	}

	handleScroll = e => {
		if (this.scrollRef.current) {
			this.scrollRef.current.scrollLeft = e.target.scrollLeft;
		}
	};

	handleResize = () => {
		animationFrame.cancel(this.rafHandle);
		this.rafHandle = animationFrame.request(this.checkOverflow);
	};

	componentWillUnmount() {
		window.removeEventListener("resize", this.handleResize);

		animationFrame.cancel(this.rafHandle);

		if (this.measureGutterAnimationFrameRequest) {
			window.cancelAnimationFrame(this.measureGutterAnimationFrameRequest);
		}
	}

	componentDidUpdate() {
		this.applyScroll();
		if (this.state.gutterWidths.length === 0 || isEqual(this.state.gutterWidths, [0, 0, 0])) {
			this.measureGutter();
		}
	}

	handleSelectAlldayEvent = (...args) => {
		//cancel any pending selections so only the event click goes through.
		this.clearSelection();
		notify(this.props.onSelectEvent, args);
	};

	handleSelectAllDaySlot = (slots, slotInfo) => {
		const { onSelectSlot } = this.props;

		const start = new Date(slots[0]);
		const end = endOfDay(new Date(slots[slots.length - 1]));

		notify(onSelectSlot, {
			slots,
			start,
			end,
			action: slotInfo.action,
			resourceId: slotInfo.resourceId,
			isAllDay: true,
		});
	};

	renderEvents(range, events, backgroundEvents, now) {
		let { min, max, components, accessors, localizer, dayLayoutAlgorithm } = this.props;

		const resources = this.memoizedResources(this.props.resources, accessors);
		const groupedEvents = resources.groupEvents(events);
		const groupedBackgroundEvents = resources.groupEvents(backgroundEvents);

		return resources.map(([id, resource], i) =>
			range.map((date, jj) => {
				let daysEvents = (groupedEvents.get(id) || []).filter(event =>
					localizer.inRange(date, accessors.start(event), accessors.end(event), "day"),
				);

				let daysBackgroundEvents = (groupedBackgroundEvents.get(id) || []).filter(event =>
					localizer.inRange(date, accessors.start(event), accessors.end(event), "day"),
				);

				return (
					<DayColumn
						{...this.props}
						localizer={localizer}
						min={localizer.merge(date, min)}
						max={localizer.merge(date, max)}
						resource={resource && id}
						components={components}
						isNow={localizer.isSameDate(date, now)}
						key={`${i}-${jj}`}
						date={date}
						events={daysEvents}
						backgroundEvents={daysBackgroundEvents}
						dayLayoutAlgorithm={dayLayoutAlgorithm}
					/>
				);
			}),
		);
	}

	render() {
		let {
			events,
			backgroundEvents,
			range,
			width,
			rtl,
			selected,
			getNow,
			resources,
			components,
			accessors,
			getters,
			localizer,
			min,
			max,
			showMultiDayTimes,
			longPressThreshold,
		} = this.props;

		const widths = width ? [width, width, width] : this.state.gutterWidths;

		let start = range[0],
			end = range[range.length - 1];

		this.slots = range.length;

		let allDayEvents = [],
			rangeEvents = [],
			rangeBackgroundEvents = [];

		events.forEach(event => {
			if (inRange(event, start, end, accessors, localizer)) {
				let eStart = accessors.start(event),
					eEnd = accessors.end(event);

				if (
					accessors.allDay(event) ||
					localizer.startAndEndAreDateOnly(eStart, eEnd) ||
					(!showMultiDayTimes && !localizer.isSameDate(eStart, eEnd))
				) {
					allDayEvents.push(event);
				} else {
					rangeEvents.push(event);
				}
			}
		});

		backgroundEvents.forEach(event => {
			if (inRange(event, start, end, accessors, localizer)) {
				rangeBackgroundEvents.push(event);
			}
		});

		allDayEvents.sort((a, b) => sortEvents(a, b, accessors, localizer));

		return (
			<div className={cx("fbc-time-view", { "fbc-time-view-resources": resources })}>
				<TimeGridHeader
					range={range}
					events={allDayEvents}
					widths={widths}
					rtl={rtl}
					getNow={getNow}
					localizer={localizer}
					selected={selected}
					resources={this.memoizedResources(resources, accessors)}
					selectable={this.props.selectable}
					accessors={accessors}
					getters={getters}
					components={components}
					scrollRef={this.scrollRef}
					isOverflowing={this.state.isOverflowing}
					longPressThreshold={longPressThreshold}
					onSelectSlot={this.handleSelectAllDaySlot}
					onSelectEvent={this.handleSelectAlldayEvent}
					onDoubleClickEvent={this.props.onDoubleClickEvent}
					onKeyPressEvent={this.props.onKeyPressEvent}
					onDrillDown={this.props.onDrillDown}
					getDrilldownView={this.props.getDrilldownView}
					timezones={this.props.timezones}
				/>
				<div ref={this.contentRef} className="fbc-time-content" onScroll={this.handleScroll}>
					{this.props.timezones.thirdTimezone && (
						<TimeGutter
							date={start}
							ref={this.thirdGutterRef}
							localizer={localizer}
							min={localizer.merge(start, min)}
							max={localizer.merge(start, max)}
							step={this.props.step}
							getNow={this.props.getNow}
							timeslots={this.props.timeslots}
							components={components}
							className="fbc-time-gutter"
							getters={getters}
							timezone={{
								source: this.props.timezones.preferredTimezone.name,
								target: this.props.timezones.thirdTimezone.name,
							}}
						/>
					)}
					{this.props.timezones.secondTimezone && (
						<TimeGutter
							date={start}
							ref={this.secondGutterRef}
							localizer={localizer}
							min={localizer.merge(start, min)}
							max={localizer.merge(start, max)}
							step={this.props.step}
							getNow={this.props.getNow}
							timeslots={this.props.timeslots}
							components={components}
							className="fbc-time-gutter"
							getters={getters}
							timezone={{
								source: this.props.timezones.preferredTimezone.name,
								target: this.props.timezones.secondTimezone.name,
							}}
						/>
					)}
					<TimeGutter
						date={start}
						ref={this.firstGutterRef}
						localizer={localizer}
						min={localizer.merge(start, min)}
						max={localizer.merge(start, max)}
						step={this.props.step}
						getNow={this.props.getNow}
						timeslots={this.props.timeslots}
						components={components}
						className="fbc-time-gutter"
						getters={getters}
					/>

					{this.renderEvents(range, rangeEvents, rangeBackgroundEvents, getNow())}
				</div>
			</div>
		);
	}

	clearSelection() {
		clearTimeout(this._selectTimer);
		this._pendingSelection = [];
	}

	measureGutter() {
		if (this.measureGutterAnimationFrameRequest) {
			window.cancelAnimationFrame(this.measureGutterAnimationFrameRequest);
		}
		this.measureGutterAnimationFrameRequest = window.requestAnimationFrame(() => {
			const firstWidth = this.firstGutterRef?.current ? getWidth(this.firstGutterRef.current) : 0;
			const secondWidth = this.secondGutterRef?.current ? getWidth(this.secondGutterRef.current) : 0;
			const thirdWidth = this.thirdGutterRef?.current ? getWidth(this.thirdGutterRef.current) : 0;

			this.setState({ gutterWidths: [firstWidth, secondWidth, thirdWidth] });
		});
	}

	applyScroll() {
		// If auto-scroll is disabled, we don't actually apply the scroll
		if (this._scrollRatio != null && this.props.enableAutoScroll === true) {
			const content = this.contentRef.current;
			content.scrollTop = content.scrollHeight * this._scrollRatio;
			// Only do this once
			this._scrollRatio = null;
		}
	}

	calculateScroll(props = this.props) {
		const { min, max, scrollToTime, localizer } = props;

		const diffMillis = scrollToTime - localizer.startOf(scrollToTime, "day");
		const totalMillis = localizer.diff(min, max, "milliseconds");

		this._scrollRatio = diffMillis / totalMillis;
	}

	checkOverflow = () => {
		if (this._updatingOverflow) return;

		const content = this.contentRef.current;
		let isOverflowing = content.scrollHeight > content.clientHeight;

		if (this.state.isOverflowing !== isOverflowing) {
			this._updatingOverflow = true;
			this.setState({ isOverflowing }, () => {
				this._updatingOverflow = false;
			});
		}
	};

	memoizedResources = memoize((resources, accessors) => Resources(resources, accessors));
}

TimeGrid.propTypes = {
	events: PropTypes.array.isRequired,
	backgroundEvents: PropTypes.array.isRequired,
	resources: PropTypes.array,

	step: PropTypes.number,
	timeslots: PropTypes.number,
	range: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
	min: PropTypes.instanceOf(Date).isRequired,
	max: PropTypes.instanceOf(Date).isRequired,
	getNow: PropTypes.func.isRequired,

	scrollToTime: PropTypes.instanceOf(Date).isRequired,
	enableAutoScroll: PropTypes.bool,
	showMultiDayTimes: PropTypes.bool,

	rtl: PropTypes.bool,
	width: PropTypes.number,

	accessors: PropTypes.object.isRequired,
	components: PropTypes.object.isRequired,
	getters: PropTypes.object.isRequired,
	localizer: PropTypes.object.isRequired,

	selected: PropTypes.object,
	selectable: PropTypes.oneOf([true, false, "ignoreEvents"]),
	longPressThreshold: PropTypes.number,

	onNavigate: PropTypes.func,
	onSelectSlot: PropTypes.func,
	onSelectEnd: PropTypes.func,
	onSelectStart: PropTypes.func,
	onSelectEvent: PropTypes.func,
	onDoubleClickEvent: PropTypes.func,
	onKeyPressEvent: PropTypes.func,
	onDrillDown: PropTypes.func,
	getDrilldownView: PropTypes.func.isRequired,

	dayLayoutAlgorithm: DayLayoutAlgorithmPropType,

	timezones: PropTypes.object,
};

TimeGrid.defaultProps = {
	step: 30,
	timeslots: 2,
};
